import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/Badge';
import type { SlaStatus } from './api';
import { formatDuration } from './duration';

/**
 * Die Restzeit einer Frist als Kennzeichnung -- F4-08, K-118.
 *
 * **Restzeit statt Faelligkeitszeitpunkt.** Der Katalog verlangt es
 * ausdruecklich, und der Grund ist die Frage, die der Bearbeiter hat:
 * "Faellig Montag 9 Uhr" beantwortet sie nicht, "noch 20 Minuten" schon.
 *
 * **Die Farbe kommt aus dem Verhaeltnis und nicht aus dem Absolutwert.** Zwei
 * Stunden Rest sind bei einer Reaktionszeit von einer Stunde undenkbar und
 * bei einer Loesungszeit von fuenf Tagen bereits knapp. Weil der Anteil in
 * der Antwort nicht steht, wird nach der Warnung des Servers gegangen: Was
 * der Waechter als Warnung vermerkt hat, ist gelb -- alles andere richtet
 * sich nach groben Schwellen, die keine Aussage ueber die Zusage machen,
 * sondern nur die Aufmerksamkeit lenken.
 */

/** Ab wann die Restzeit optisch draengt -- eine Stunde. */
const URGENT_SECONDS = 60 * 60;

/** Ab wann sie zumindest auffaellt -- vier Stunden. */
const NEAR_SECONDS = 4 * 60 * 60;

interface SlaBadgeProps {
  sla: SlaStatus | null | undefined;
  /** Welche der beiden Uhren gezeigt wird. */
  target?: 'resolution' | 'first_response';
}

export function SlaBadge({ sla, target = 'resolution' }: SlaBadgeProps) {
  const { t } = useTranslation();

  if (!sla?.sla_id) {
    return <span className="table__muted">{t('sla.none')}</span>;
  }

  const remaining =
    target === 'resolution'
      ? (sla.resolution_remaining_seconds ?? null)
      : (sla.first_response_remaining_seconds ?? null);

  if (remaining === null) {
    // Die Reaktionszeit ist beantwortet oder die Loesungszeit erfuellt --
    // beides ist ein Ergebnis und kein fehlender Wert.
    return <Badge tone="success">{t('sla.met')}</Badge>;
  }

  if (sla.is_paused) {
    return <Badge tone="neutral">{t('sla.paused')}</Badge>;
  }

  if (remaining < 0) {
    return (
      <Badge tone="danger" dot>
        {t('sla.overdueBy', { duration: formatDuration(-remaining, t) })}
      </Badge>
    );
  }

  return <Badge tone={toneFor(remaining)}>{formatDuration(remaining, t)}</Badge>;
}

function toneFor(remaining: number): BadgeTone {
  if (remaining <= URGENT_SECONDS) return 'danger';
  if (remaining <= NEAR_SECONDS) return 'warning';
  return 'success';
}
